import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Tags, Plus, Trash2, Save, Sparkles, Check, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function KenmerkenImport({ onDone }) {
  const [kFile, setKFile] = useState(null);
  const [klFile, setKlFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (!kFile || !klFile) { toast.error("Kies beide bestanden: kenmerken.csv én klanten.csv"); return; }
    const fd = new FormData();
    fd.append("kenmerken", kFile);
    fd.append("klanten", klFile);
    setBusy(true);
    try {
      const { data } = await api.post(`/kenmerken/import-csv?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (dryRun) toast.success(`Preview: ${data.kenmerken_toegevoegd} kenmerken bij ${data.organisaties_bijgewerkt} organisaties`);
      else { toast.success(`${data.kenmerken_toegevoegd} kenmerken geïmporteerd`); onDone?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import mislukt"); }
    finally { setBusy(false); }
  };

  const Stat = ({ label, value }) => (
    <div className="rounded-lg bg-secondary/60 px-3 py-2"><div className="text-[11px] text-muted-foreground">{label}</div><div className="font-medium tabular-nums">{value}</div></div>
  );

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="kenmerk-import">
      <div>
        <h2 className="font-display text-xl font-medium flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Kenmerken importeren (CSV)</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Upload de PerfectView-exports <code className="font-mono text-[11px] bg-secondary px-1 rounded">kenmerken.csv</code> en <code className="font-mono text-[11px] bg-secondary px-1 rounded">klanten.csv</code>. De verhouding uit kenmerken wordt via klanten gekoppeld aan de organisatie. Categorieën en waarden worden aangevuld; bestaande kenmerken blijven staan (idempotent).
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">kenmerken.csv</Label><Input type="file" accept=".csv" onChange={(e) => setKFile(e.target.files?.[0] || null)} data-testid="kenmerk-import-kenmerken" /></div>
        <div><Label className="text-xs">klanten.csv</Label><Input type="file" accept=".csv" onChange={(e) => setKlFile(e.target.files?.[0] || null)} data-testid="kenmerk-import-klanten" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={busy || !kFile || !klFile} onClick={() => run(true)} data-testid="kenmerk-import-preview">Preview (dry-run)</Button>
        {result?.dry_run && result.kenmerken_toegevoegd > 0 && (
          <Button size="sm" disabled={busy} onClick={() => run(false)} data-testid="kenmerk-import-apply">Toepassen · {result.kenmerken_toegevoegd} kenmerken</Button>
        )}
      </div>
      {result && (
        <div className="space-y-2" data-testid="kenmerk-import-result">
          <div className="text-xs font-medium text-muted-foreground">{result.dry_run ? "Preview — niets gewijzigd" : "Toegepast"}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <Stat label="Kenmerk-rijen" value={result.kenmerk_rijen} />
            <Stat label="Gekoppeld" value={result.gekoppeld} />
            <Stat label="Organisaties" value={result.organisaties_bijgewerkt} />
            <Stat label="Nieuwe kenmerken" value={result.kenmerken_toegevoegd} />
            <Stat label="Categorieën" value={`${result.categorieen} (${result.nieuwe_categorieen} nieuw)`} />
            <Stat label="Nieuwe waarden" value={result.nieuwe_waarden} />
            <Stat label="Geen verhouding" value={result.overgeslagen_geen_verhouding} />
            <Stat label="Organisatie onbekend" value={result.overgeslagen_geen_organisatie} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function InstellingenKenmerken() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [defs, setDefs] = useState([]);
  const [aiConfig, setAiConfig] = useState({ auto_suggest: false });
  const [suggesties, setSuggesties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ categorie: "", waardenTekst: "", ai_actief: true });

  const load = async () => {
    const [d, c, s] = await Promise.all([
      api.get("/kenmerk-definities"),
      api.get("/kenmerken/ai-config"),
      api.get("/kenmerk-suggesties?status=open"),
    ]);
    setDefs(d.data || []);
    setAiConfig(c.data || { auto_suggest: false });
    setSuggesties(s.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveAi = async (val) => {
    setAiConfig({ auto_suggest: val });
    try { await api.put("/kenmerken/ai-config", { auto_suggest: val }); toast.success("AI-instelling opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
  };

  const openNew = () => { setForm({ categorie: "", waardenTekst: "", ai_actief: true, id: null }); setOpen(true); };
  const openEdit = (d) => { setForm({ id: d.id, categorie: d.categorie, waardenTekst: (d.waarden || []).join(", "), ai_actief: d.ai_actief }); setOpen(true); };
  const saveDef = async () => {
    const payload = {
      categorie: form.categorie.trim(),
      waarden: form.waardenTekst.split(",").map(s => s.trim()).filter(Boolean),
      ai_actief: form.ai_actief,
    };
    if (!payload.categorie) { toast.error("Categorie verplicht"); return; }
    try {
      if (form.id) await api.patch(`/kenmerk-definities/${form.id}`, payload);
      else await api.post("/kenmerk-definities", payload);
      setOpen(false); await load(); toast.success("Opgeslagen");
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const delDef = async (d) => {
    if (!window.confirm(`Categorie "${d.categorie}" verwijderen?`)) return;
    await api.delete(`/kenmerk-definities/${d.id}`); await load(); toast.success("Verwijderd");
  };

  const approve = async (s) => { await api.post(`/kenmerk-suggesties/${s.id}/goedkeuren`); setSuggesties(x => x.filter(i => i.id !== s.id)); toast.success("Toegevoegd"); };
  const reject = async (s) => { await api.post(`/kenmerk-suggesties/${s.id}/afwijzen`); setSuggesties(x => x.filter(i => i.id !== s.id)); };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="instellingen-kenmerken">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center"><Tags className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Kenmerken</h1>
          <p className="text-sm text-muted-foreground mt-1">Beheer categorieën en waarden, en laat de AI kenmerken voorstellen op basis van CRM-activiteit.</p>
        </div>
      </div>

      {/* AI-instelling */}
      <div className="bg-card border border-border rounded-xl p-6" data-testid="kenmerk-ai-config">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-medium flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI-suggesties</h2>
            <p className="text-sm text-muted-foreground mt-1">Stel kenmerken automatisch voor wanneer order-/organisatiegegevens wijzigen. Voorstellen komen altijd eerst ter beoordeling.</p>
          </div>
          <Switch checked={!!aiConfig.auto_suggest} onCheckedChange={isAdmin ? saveAi : undefined} disabled={!isAdmin} data-testid="kenmerk-autosuggest-switch" />
        </div>
      </div>

      {/* Openstaande suggesties */}
      {suggesties.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="kenmerk-review-lijst">
          <h2 className="font-display text-xl font-medium">Te beoordelen ({suggesties.length})</h2>
          {suggesties.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0">
              <div className="flex-1 text-sm">
                <Link to={`/organisaties/${s.organisatie_id}`} className="font-medium hover:underline">{s.organisatie_naam}</Link>
                <span className="text-muted-foreground"> · {s.categorie}: </span>
                <span className="font-medium">{s.waarde}</span>
                {s.reden && <div className="text-xs text-muted-foreground">{s.reden}</div>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => approve(s)} data-testid={`review-approve-${s.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => reject(s)} data-testid={`review-reject-${s.id}`}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdmin && <KenmerkenImport onDone={load} />}

      {/* Definities */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-medium">Categorieën ({defs.length})</h2>
          {isAdmin && <Button size="sm" onClick={openNew} data-testid="kenmerk-new-def-btn"><Plus className="h-4 w-4 mr-1" />Nieuwe categorie</Button>}
        </div>
        <div className="divide-y divide-border">
          {defs.map(d => (
            <div key={d.id} className="py-3 flex items-start justify-between gap-4" data-testid={`def-${d.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{d.categorie}</span>
                  {d.ai_actief ? <Badge variant="secondary" className="text-[10px]">AI aan</Badge> : <Badge variant="outline" className="text-[10px]">AI uit</Badge>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(d.waarden || []).slice(0, 20).map(w => <Badge key={w} variant="outline" className="font-normal text-xs">{w}</Badge>)}
                  {(d.waarden || []).length > 20 && <span className="text-xs text-muted-foreground">+{d.waarden.length - 20} meer</span>}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`def-edit-${d.id}`}>Bewerk</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => delDef(d)} data-testid={`def-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
          {defs.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Nog geen categorieën.</div>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Categorie bewerken" : "Nieuwe categorie"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Categorienaam</Label><Input value={form.categorie} onChange={(e) => setForm(f => ({ ...f, categorie: e.target.value }))} data-testid="def-form-cat" /></div>
            <div><Label>Waarden (komma-gescheiden)</Label><Input value={form.waardenTekst} placeholder="bv. Drogist, Apotheek, Webshop" onChange={(e) => setForm(f => ({ ...f, waardenTekst: e.target.value }))} data-testid="def-form-waarden" /></div>
            <div className="flex items-center justify-between"><Label>AI mag suggesties doen voor deze categorie</Label><Switch checked={form.ai_actief} onCheckedChange={(v) => setForm(f => ({ ...f, ai_actief: v }))} data-testid="def-form-ai" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={saveDef} data-testid="def-form-save"><Save className="h-4 w-4 mr-1" />Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
